import { BigNumber, Contract, ContractFactory, providers, Signer } from 'ethers';

import { Token } from '..';
import { Role } from './enum';
import { Contracts } from '../token/contracts';
import { Registry } from '../token/registry';

export default { contractFactory };

export function contractFactory(contract: { abi: string; bytecode: string; signer: Signer }): ContractFactory {
  return new ContractFactory(contract.abi, contract.bytecode, contract.signer);
}

export async function deployContract(contract: { abi: string; bytecode: string; signer: Signer }): Promise<Contract> {
  const factory = contractFactory(contract);
  const deploy = await factory.deploy();
  return await deploy.deployed();
}

export async function connectAt(contract: { contractAddress: string; abi: Array<string>; caller: Signer | providers.Provider }): Promise<Contract> {
  if (!Signer.isSigner(contract.caller) && !providers.Provider.isProvider(contract.caller)) {
    throw new Error('Neither Signer or Provider has been specified');
  }

  return new Contract(contract.contractAddress, contract.abi, contract.caller);
}

export function signerOrProvider(caller: TransactionOptionsInterface): Signer | providers.Provider {
  if (Signer.isSigner(caller.signer)) {
    return caller.signer as Signer;
  } else if (providers.Provider.isProvider(caller.provider)) {
    return caller.provider as providers.Provider;
  }
  throw Error('Neither Signer or Provider has been specified');
}

export interface ContractsFactoryInterface {
  token: string;
  compliance: string;
  claimTopics: string;
  trustedIssuers: string;
  identityRegistry: string;
  identityStorage: string;
  dvd: string;
  implementationAuthority: string;
}

export interface TransactionOptionsInterface {
  role?: Role;
  signer?: Signer;
  provider?: providers.Provider;
  overrides?: OverrideTransactionsInterface;
}

/**
 * OverrideTransactionsInterface
 */
export interface OverrideTransactionsInterface {
  /**
   * The maximum units of gas for the transaction to use
   */
  gasLimit?: number;
  /**
   * The price (in wei) per unit of gas
   */
  gasPrice?: BigNumber;
  /**
   * The nonce to use in the transaction
   */
  nonce?: number;
  /**
   * The amount to send with the transaction (i.e. msg.value)
   */
  value?: BigNumber;
  /**
   * The chain ID (or network ID) to use
   */
  chainId?: number;
}

async function checkRolePermission(signer: string, token: Token | Contracts | Registry, role: string): Promise<void> {
  switch (role) {
    case Role.IdentityAgent:
      if (!(await token.isIdentityRegistryAgent(signer))) {
        throw new Error('Need an Identity Agent to run this method');
      }
      break;
    case Role.TokenAgent:
      if (!(await token.isTokenAgent(signer))) {
        throw new Error('Need a Token Agent to run this method');
      }
      break;
    case Role.IdentityOwner:
      if ((await token.getOwnerOfIdentityRegistry()) !== signer) {
        throw new Error('Need to be Owner of the Identity Registry to run this method');
      }
      break;
    case Role.StorageOwner:
      if ((await token.getOwnerOfIdentityRegistryStorage()) !== signer) {
        throw new Error('Need to be Owner of the Storage Registry to run this method');
      }
      break;
    case Role.ImplementationAuthorityOwner:
      if ((await token.getOwnerOfImplementationAuthority()) !== signer) {
        throw new Error('Need to be Owner of the Implementation Authority to run this method');
      }
      break;
    case Role.DVDOwner:
      if ((await token.getOwnerOfDVD()) !== signer) {
        throw new Error('Need to be Owner of the DVD contract to run this method');
      }
      break;
    case Role.ComplianceOwner:
      if ((await token.getOwnerOfCompliance()) !== signer) {
        throw new Error('Need to be Owner of the Compliance to run this method');
      }
      break;
    case Role.TrustedIssuersOwner:
      if ((await token.getOwnerOfTrustedIssuersRegistry()) !== signer) {
        throw new Error('Need to be Owner of the Trusted Issuers Registry to run this method');
      }
      break;
    case Role.ClaimTopicsOwner:
      if ((await token.getOwnerOfClaimTopicsRegistry()) !== signer) {
        throw new Error('Need to be Owner of the Claim Topics Registry to run this method');
      }
      break;
    case Role.TokenOwner:
      if ((await token.getOwnerOfToken()) !== signer) {
        throw new Error('Need to be Owner of the Token to run this method');
      }
      break;
    default:
      throw new Error('No Role specified');
  }
}

export async function loadContractWithCaller(
  caller: TransactionOptionsInterface,
  contract: Contract,
  role?: Role,
  token?: Token | Contracts,
): Promise<Contract> {
  if (caller.signer) {
    if (role && token) {
      await checkRolePermission(await caller.signer.getAddress(), token, role);
    }
    return contract.connect(caller.signer);
  } else if (caller.provider) {
    if (role) {
      throw new Error('Need a Signer to run this method');
    }
    return new Contract(contract.address, contract.interface, caller.provider);
  }
  throw new Error('Need at least one caller');
}

export function getTransactionOptions(instance: Token | Contracts | Registry, transactionOptions: TransactionOptionsInterface): TransactionOptionsInterface {
  transactionOptions.signer = transactionOptions.signer ?? instance.signer;
  transactionOptions.provider = transactionOptions.provider ?? instance.provider;
  transactionOptions.overrides = transactionOptions.overrides ?? instance.overrides;
  return transactionOptions;
}
